import React from "react";
import styled, { keyframes } from "styled-components";
import { FiAlertCircle, FiInbox } from "react-icons/fi";
import { Action } from "../../pages/MVP/shared";
import { useLanguage } from "../../contexts/LanguageContext";

const pulse = keyframes`0%,100%{opacity:.5}50%{opacity:1}`;

export const Skeleton = styled.div<{ $h?: number; $w?: string; $r?: number }>`
  height: ${(p) => p.$h ?? 16}px;
  width: ${(p) => p.$w ?? "100%"};
  border-radius: ${(p) => p.$r ?? 8}px;
  background: ${(p) => p.theme.colors.surfaceHover};
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const Box = styled.div<{ $tone: "empty" | "error" }>`
  display: grid;
  justify-items: center;
  text-align: center;
  gap: 10px;
  padding: 32px 20px;
  border-radius: 14px;
  border: 1px dashed
    ${(p) => (p.$tone === "error" ? "#ff737340" : p.theme.colors.border)};
  color: ${(p) => p.theme.colors.textSecondary};
  > svg {
    width: 30px;
    height: 30px;
    color: ${(p) =>
      p.$tone === "error" ? p.theme.colors.danger : p.theme.colors.textMuted};
  }
  h3 {
    color: ${(p) => p.theme.colors.text};
    font-size: 16px;
  }
  p {
    max-width: 44ch;
    font-size: 14px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;

interface Props {
  kind: "loading" | "empty" | "error";
  title?: string;
  text?: string;
  /** Для loading: сколько строк-скелетов показать. */
  rows?: number;
  actionLabel?: string;
  onAction?: () => void;
}

/** Единые состояния асинхронного блока: loading / empty / error. */
export default function StateBlock({
  kind,
  title,
  text,
  rows = 4,
  actionLabel,
  onAction,
}: Props) {
  const { t } = useLanguage();
  if (kind === "loading") {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={title || t("common.loading")}
        style={{ display: "grid", gap: 12 }}
      >
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} $h={48} $r={12} />
        ))}
      </div>
    );
  }
  const error = kind === "error";
  return (
    <Box $tone={error ? "error" : "empty"} role={error ? "alert" : undefined}>
      {error ? <FiAlertCircle /> : <FiInbox />}
      <h3>{title || (error ? t("common.error_generic") : t("common.empty"))}</h3>
      {text && <p>{text}</p>}
      {onAction && (
        <Action type="button" onClick={onAction}>
          {actionLabel || (error ? t("common.retry") : t("common.reset"))}
        </Action>
      )}
    </Box>
  );
}
